import { useEffect, useState } from "react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import { Bell, Save, Loader2, ShieldOff } from "lucide-react";

function Toggle({ label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start justify-between gap-6 py-4 border-b border-sand-100 cursor-pointer select-none">
      <span>
        <span className="block text-sm font-medium text-sand-900">{label}</span>
        {hint && <span className="block text-xs text-sand-500 mt-0.5 max-w-md">{hint}</span>}
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)}
        className="mt-1 accent-ember w-4 h-4 shrink-0" data-testid={testId} />
    </label>
  );
}

export default function YouNotifications() {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const { logout } = useAuth();

  const signOutEverywhere = async () => {
    if (!window.confirm("Sign out on all devices? Every browser and mobile session will need to sign in again.")) return;
    setSigningOut(true);
    try {
      await api.post("/auth/logout-all");
      toast.success("Signed out on all devices");
      await logout();
    } catch {
      toast.error("Could not sign out everywhere — try again");
      setSigningOut(false);
    }
  };

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => setPrefs(data.notifications))
      .catch(() => toast.error("Could not load settings"));
  }, []);

  const set = (patch) => setPrefs((p) => ({ ...p, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", { notifications: prefs });
      setPrefs(data.notifications);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || "Could not save settings");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-3xl mx-auto px-6 md:px-12 py-8 md:py-12" data-testid="settings-page">
      <div className="mb-8">
        <p className="label-tag mb-3">You · Notifications</p>
        <h1 className="heading-h1 text-4xl sm:text-5xl mb-2">Notifications.</h1>
        <p className="text-sand-600 text-base mt-3">Choose what Wandr sends you, and where.</p>
        <div className="divider-strong mt-4" />
      </div>

      {prefs === null ? (
        <div className="rounded-card border border-hairline bg-surface h-72 animate-pulse" />
      ) : (
        <section className="bg-surface rounded-card border border-hairline p-6 md:p-8">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={15} className="text-ember" />
            <h2 className="font-display font-medium text-xl tracking-tight">Notification preferences</h2>
          </div>
          <Toggle label="Trip reminders (push)" hint="Mobile push 24 hours before each flight, check-in, pickup, or event."
            checked={prefs.trip_reminders_push} onChange={(v) => set({ trip_reminders_push: v })} testId="setting-trip-reminders" />
          <Toggle label="Weekly digest (email)" hint="Monday summary of parsed emails, pushes, and wishlist nudges."
            checked={prefs.weekly_digest_email} onChange={(v) => set({ weekly_digest_email: v })} testId="setting-weekly-digest" />
          <Toggle label="Document expiry alerts (email + dashboard)" hint="Warns when a passport, Global Entry, or license nears expiry — or expires before an upcoming trip."
            checked={prefs.doc_expiry_alerts} onChange={(v) => set({ doc_expiry_alerts: v })} testId="setting-doc-alerts" />
          {prefs.doc_expiry_alerts && (
            <div className="flex items-center gap-3 py-4 border-b border-sand-100 pl-4">
              <span className="text-sm text-sand-600">Warn me</span>
              <input type="number" min={1} max={24} value={prefs.doc_expiry_months}
                onChange={(e) => set({ doc_expiry_months: Number(e.target.value) || 1 })}
                className="w-16 border border-sand-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-ember"
                data-testid="setting-doc-months" />
              <span className="text-sm text-sand-600">months before expiry</span>
            </div>
          )}
          <Toggle label="Parse confirmations (email)" hint="A short email whenever reservations are saved from a forwarded email."
            checked={prefs.parse_confirmation_email} onChange={(v) => set({ parse_confirmation_email: v })} testId="setting-parse-confirmation" />
          <div className="mt-8 flex justify-end">
            <button onClick={save} disabled={busy} className="btn-swiss btn-primary text-sm disabled:opacity-50" data-testid="settings-save">
              {busy ? <Loader2 size={15} className="animate-spin mr-2" /> : <Save size={15} className="mr-2" />}
              {busy ? "Saving…" : "Save settings"}
            </button>
          </div>
        </section>
      )}

      <section className="bg-surface rounded-card border border-hairline p-6 md:p-8 mt-8" data-testid="security-section">
        <div className="flex items-center gap-2 mb-2">
          <ShieldOff size={15} className="text-ember" />
          <h2 className="font-display font-medium text-xl tracking-tight">Security</h2>
        </div>
        <div className="flex items-start justify-between gap-6 py-4">
          <span>
            <span className="block text-sm font-medium text-sand-900">Sign out everywhere</span>
            <span className="block text-xs text-sand-500 mt-0.5 max-w-md">
              Revokes every active session — all browsers and the mobile app will need to sign in again. Use this if a device was lost or you suspect account access.
            </span>
          </span>
          <button onClick={signOutEverywhere} disabled={signingOut}
            className="btn-swiss text-sm border border-red-600 text-red-600 hover:bg-red-600 hover:text-white disabled:opacity-50 shrink-0"
            data-testid="logout-all-btn">
            {signingOut ? <Loader2 size={15} className="animate-spin mr-2" /> : null}
            {signingOut ? "Signing out…" : "Sign out everywhere"}
          </button>
        </div>
      </section>
    </main>
  );
}
